import { makeAutoObservable, runInAction } from 'mobx';
import { Notification } from 'ksnm-common-ui/lib/Notification';
import { v4 } from 'uuid';
import type { SelectRows } from 'ksnm-common-ui/lib/Table2/contexts/Table/TableContext.interface';
import type { ResponseDataRow } from 'ksnm-common-ui/lib/Table2/utils/TableRequest';

import { cmApi, customRequest, fetchWithAudit } from '@src/api';
import { downloadAsFile, downloadCustomFile, getFileName } from '@utils';
import type {
  CmApi,
  CompanyReportDto,
  ErrorResponse,
  EtlProcessDto,
  ReportingBlockDto,
  ReportPackageAddonDto,
  ReportPackageInfoDto,
  ReportPublishDto,
  RequestParams,
  UncreatedReportDto,
  XmlDto,
} from '@src/api';

import { auditStore } from '../auditStore';
import { preloaderStore } from '../preloaderStore';

interface IPackageFormInitial {
  year?: string;
  correction?: string;
  periodCode?: string;
  organizationCode?: string;
  reportPackageCode?: string;
  reportGroupCode?: string;
  name?: string;
  declarationRegNumber?: string;
  codeExt?: string;
  description?: string;
  code?: string;
  disclosureData?: string;
  creationData?: string;
  status?: string;
  username?: string;
}

export interface IUncreatedReportRequest {
  organizationCode?: string;
  reportPackageCode?: string;
  reportGroupCode?: string;
  periodCode?: string;
  reportPackageYear?: number;
}

interface ISelected {
  selectRows: SelectRows;
  rows: ResponseDataRow[];
}

interface IRejectModal {
  packageCode: string;
  reports: (Omit<ReportingBlockDto, 'id'> & { id: string })[];
}

interface IDownloadReports {
  downloadType: 'XLSX' | 'BLANK_XLSX';
  reports: ReportingBlockDto[];
}

type UncreatedReportDtoDefId = UncreatedReportDto & { id: string };

class PublicationRegisterStore {
  publicationRegister: CompanyReportDto[] = [];

  reportPackageInfo: ReportPackageInfoDto = {};

  isFormed = false;

  packageFormInitial: IPackageFormInitial = {};

  organizationCode = '';

  uncreatedReports: UncreatedReportDtoDefId[] = [];

  errors: string[] = [];

  openErrorsModal = false;

  isEditPackageMode = false;

  selected: ISelected | null = null;

  rejectModal: IRejectModal | null = null;

  downloadReports: IDownloadReports | null = null;

  api: CmApi;

  constructor({ api }: { api: CmApi }) {
    this.api = api;
    makeAutoObservable(this);
  }

  fetch = async (organizationCode?: string): Promise<void> => {
    preloaderStore.preloader(true);
    try {
      const { data } = await this.api.companyReports.getCompanyReport(organizationCode ?? this.organizationCode);
      runInAction(() => {
        this.publicationRegister = data.id ? [data] : [];
        if (organizationCode) this.organizationCode = organizationCode;
      });
    } catch (error) {
      console.error(error);
    }
    preloaderStore.preloader(false);
  };

  clear = () => {
    this.publicationRegister = [];
    this.selected = null;
  };

  getPackageInfo = async (
    reportPackageCode: string,
    reportCodeExt?: string,
    reportVersionCode?: string,
  ): Promise<void> => {
    preloaderStore.preloader(true);
    try {
      const { data } = await this.api.companyReports.getReportPackageInfo({
        reportPackageCode,
        reportCodeExt,
        reportVersionCode,
      });
      runInAction(() => {
        this.reportPackageInfo = data ?? [];
      });
    } catch (error) {
      console.error(error);
    }
    preloaderStore.preloader(false);
  };

  setIsFormed = (value: boolean) => {
    this.isFormed = value;
  };

  setPackageFormInitials = (value: IPackageFormInitial) => {
    this.packageFormInitial = value;
  };

  toggleErrorsModal = () => {
    this.openErrorsModal = !this.openErrorsModal;
  };

  setEditPackageMode = (value: boolean) => {
    this.isEditPackageMode = value;
  };

  setSelected = (value) => {
    this.selected = value;
  };

  setRejectModal = (value: IRejectModal) => {
    this.rejectModal = value;
  };

  setDownloadReports = (value: IDownloadReports | null) => {
    this.downloadReports = value;
  };

  savePackage = async (reportPackage: ReportPackageAddonDto) => {
    preloaderStore.preloader(true);
    try {
      const { data } = await (this.isEditPackageMode
        ? this.api.reportPackage.updateReportPackage(reportPackage)
        : fetchWithAudit(
            this.api.reportPackage.addReportPackage(reportPackage),
            {
              moduleName: 'Calculator',
              serviceName: 'Reporting_preparation',
              operationName: 'Create_package',
              message: `Создать пакет. Пакет ${reportPackage.code}`,
            },
            200,
          ));

      if (data) {
        preloaderStore.preloader(false);
        return data;
      }
    } catch (error) {
      console.error(error);
    }
    preloaderStore.preloader(false);
  };

  getUncreatedReports = async (
    uncreatedReportRequest: IUncreatedReportRequest,
  ): Promise<UncreatedReportDto[] | undefined> => {
    preloaderStore.preloader(true);
    try {
      const { data } = await this.api.uncreatedReports.getUncreatedReports({
        reportPackageCode: uncreatedReportRequest.reportPackageCode ?? '',
        reportGroupCode: uncreatedReportRequest.reportGroupCode ?? '',
        periodCode: uncreatedReportRequest.periodCode ?? '',
        reportPackageYear: uncreatedReportRequest.reportPackageYear ?? 0,
        organizationCode: this.organizationCode,
      });

      if (data) {
        runInAction(() => {
          if (data)
            this.uncreatedReports = data.map((item) => ({
              ...item,
              id: v4(),
              name: `${item.reportVersionCode} - ${item.reportVersionName}`,
            }));
        });
        preloaderStore.preloader(false);
        return data;
      }
    } catch (error) {
      console.error(error);
    }
    preloaderStore.preloader(false);
  };

  addUncreatedReportsIntoPackage = async (reports: ReportPublishDto[]) => {
    preloaderStore.preloader(true);
    try {
      const response = await fetchWithAudit(
        this.api.uncreatedReports.addUncreatedReportsIntoPackage(reports),
        {
          moduleName: 'Calculator',
          serviceName: 'Reporting_preparation',
          operationName: 'Add_uncreated_report_to_package',
          message: `В пакет ${reports[0].reportPackageCode} добавлены отче${reports.length === 1 ? 'т' : 'ты'} ${reports
            .map((item) => item.reportVersionCode)
            .join(', ')}.`,
        },
        200,
      );

      if (response.status === 200) {
        preloaderStore.preloader(false);
        this.clear(); // для сброса таблицы
        await this.fetch();
        return 'success';
      }
    } catch (error) {
      console.error(error);
    }
    preloaderStore.preloader(false);
  };

  getExcel = async (organizationName: string, reportPackageCode: string, reportVersionCode: string): Promise<void> => {
    preloaderStore.preloader(true);
    try {
      const response = await this.api.getExcel.getExcel(organizationName, reportPackageCode, reportVersionCode);
      const contentDisposition = response.headers.get('Content-Disposition');
      const fileName = contentDisposition
        ? getFileName(contentDisposition) ?? `${reportPackageCode}.xlsx`
        : `${reportPackageCode}.xlsx`;
      downloadAsFile(await response.blob(), fileName);
    } catch (error) {
      console.error(error);
    }
    preloaderStore.preloader(false);
  };

  getExcelStream = async (
    reports: { organizationCode: string; reportPackageCode: string; reportVersionCode: string }[],
  ): Promise<void> => {
    for (const [i, report] of reports.entries()) {
      try {
        const response = await customRequest({
          path: `/getExcelStream/${encodeURI(report.organizationCode)}/${encodeURI(
            report.reportPackageCode,
          )}/${encodeURI(report.reportVersionCode)}`,
          method: 'GET',
        });

        if (response.status === 200) {
          auditStore.createAuditData({
            moduleName: 'Calculator',
            serviceName: 'Reporting_preparation',
            operationName: 'Download_report',
            message: `Скачивание отчёта. Пакет ${report.reportPackageCode}, версия ${report.reportVersionCode}`,
          });
        }

        await downloadCustomFile({
          response,
          downloadedFileName: `${report.organizationCode}-${report.reportPackageCode}-${report.reportVersionCode}.xls`,
          match: /filename\*=UTF-8''(.+)$/,
          mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel',
        });
      } catch (error) {
        console.error(error);
      }

      if (i === reports.length - 1) {
        Notification({
          message: 'Выгрузка отчетов завершена',
          type: 'success',
        });
      }
    }
  };

  getBlankExcel = async (
    reports: { organizationCode: string; reportPackageCode: string; reportVersionCode: string }[],
  ): Promise<void> => {
    for (const [i, report] of reports.entries()) {
      try {
        const response = await customRequest({
          path: `/getBlankExcel/${encodeURI(report.organizationCode)}/${encodeURI(
            report.reportPackageCode,
          )}/${encodeURI(report.reportVersionCode)}`,
          method: 'GET',
        });

        const contentDisposition = response.headers.get('Content-Disposition');
        const fileName = contentDisposition
          ? getFileName(contentDisposition) ?? `${report.reportPackageCode}.xlsx`
          : `${report.reportPackageCode}.xlsx`;
        downloadAsFile(await response.blob(), fileName);
      } catch (error) {
        console.error(error);
      }

      if (i === reports.length - 1) {
        Notification({
          message: 'Выгрузка отчетов завершена',
          type: 'success',
        });
      }
    }
  };

  uploadExcel = async (
    organizationName: string,
    reportPackageCode: string,
    reportVersionCode: string,
    excelFile: string,
  ) => {
    preloaderStore.preloader(true);
    try {
      const response = await this.api.uploadExcel.uploadExcel({
        organizationName,
        reportPackageCode,
        reportVersionCode,
        excelFile,
      });

      if (response.status === 200) {
        Notification({
          message: 'Файл был загружен',
          type: 'success',
        });

        auditStore.createAuditData({
          moduleName: 'Calculator',
          serviceName: 'Reporting_preparation',
          operationName: 'Upload_report',
          message: `Загрузка отчёта. Пакет ${reportPackageCode}, версия ${reportVersionCode}`,
        });

        preloaderStore.preloader(false);
        return 'success';
      }
    } catch (error) {
      console.error(error);
    }
    preloaderStore.preloader(false);
  };

  uploadXML = async (upload: { packageCode?: string; versionCode?: string; file?: File }) => {
    preloaderStore.preloader(true);
    try {
      const { data } = await this.api.xml.postXml(upload, { path: '/xml/upload' } as RequestParams); // бек не смог сгенерить запрос :(, поэтому кастомный url /xml/upload
      const errors = (data as XmlDto)?.errors;

      if (errors?.length) {
        runInAction(() => {
          this.errors = errors;
          this.openErrorsModal = true;
        });
      }

      if (!errors?.length) {
        Notification({
          message: 'Файл был загружен',
          type: 'success',
        });
        this.fetch();
      }
    } catch (error) {
      console.error(error);
    }
    preloaderStore.preloader(false);
  };

  deletePackage = async (reportPackageCode: string) => {
    preloaderStore.preloader(true);
    try {
      await fetchWithAudit(
        this.api.reportPackage.deleteReportPackageByCode(reportPackageCode),
        {
          moduleName: 'Calculator',
          serviceName: 'Reporting_preparation',
          operationName: 'Delete_package',
          message: `Удаление пакета. Пакет ${reportPackageCode}`,
        },
        200,
      );

      this.clear();
      await this.fetch();
    } catch (error) {
      console.error(error);
    }
    preloaderStore.preloader(false);
  };

  sendReportPackageOnApprove = async (reportPackageCode: string) => {
    preloaderStore.preloader(true);
    try {
      await this.api.reportPackage.sendReportPackageOnApprove(reportPackageCode);
      this.clear();
      await this.fetch();
    } catch (error) {
      console.error(error);
    }
    preloaderStore.preloader(false);
  };

  updateReportPackageStatus = async (
    reportPackageCode: string,
    status: string,
    noData = false,
  ): Promise<string[] | string | void> => {
    preloaderStore.preloader(true);
    try {
      const { data, error } = await this.api.reportPackage.updateReportPackageStatus(reportPackageCode, {
        status,
        noData: status === 'Опубликован' ? noData : true,
      });

      if (
        error &&
        (error?.error as unknown as ErrorResponse)?.errors?.[0]?.message.includes('Невозможно отклонить пакет')
      ) {
        preloaderStore.preloader(false);
        return 'Невозможно отклонить пакет';
      }

      if (data?.length > 0) {
        preloaderStore.preloader(false);
        return data;
      }

      if (!data?.length) await this.fetch();
    } catch (error) {
      console.error(error);
    }
    preloaderStore.preloader(false);
  };

  returnReportPackageToWork = async (reportPackageCode: string) => {
    preloaderStore.preloader(true);
    try {
      await this.api.reportPackage.returnReportPackageToWork(reportPackageCode);
      await this.fetch();
    } catch (error) {
      console.error(error);
    }
    preloaderStore.preloader(false);
  };

  updateReportPublish = async (report: ReportPublishDto) => {
    preloaderStore.preloader(true);

    try {
      await this.api.reportPublish.updateReportPublish(report);
      await this.fetch();
    } catch (error) {
      console.error(error);
    }
    preloaderStore.preloader(false);
  };

  cleanReport = async (reportPackageCode: string, reportVersionCode: string) => {
    preloaderStore.preloader(true);
    try {
      await this.api.reportPublish.cleanReport({
        reportPackageCode,
        reportVersionCode,
      });
      await this.fetch();
    } catch (error) {
      console.error(error);
    }
    preloaderStore.preloader(false);
  };

  cleanReportPackage = async (reportPackageCode: string) => {
    preloaderStore.preloader(true);
    try {
      await fetchWithAudit(
        this.api.reportPackage.cleanReportPackageByCode({
          reportPackageCode,
        }),
        {
          moduleName: 'Calculator',
          serviceName: 'Reporting_preparation',
          operationName: 'Clean_package',
          message: `Очистка пакета. Пакет ${reportPackageCode}`,
        },
        200,
      );

      await this.fetch();
    } catch (error) {
      console.error(error);
    }
    preloaderStore.preloader(false);
  };

  calculateRegisters = async (
    reportPackageCode: string,
    reportVersionCodes: string[],
  ): Promise<EtlProcessDto | undefined> => {
    preloaderStore.preloader(true);

    try {
      const response = await fetchWithAudit(
        this.api.calculate.calculateData({
          reportPackageCode,
          reportVersionCodes,
        }),
        {
          moduleName: 'Calculator',
          serviceName: 'Reporting_preparation',
          operationName: 'Start_calculation',
          message: `Запуск процедуры расчёта по отчёту в Nifi. Пакет ${reportPackageCode}`,
        },
        200,
      );

      if (response.status === 200) {
        preloaderStore.preloader(false);
        return response.data;
      }
    } catch (error) {
      console.error(error);
    }
    preloaderStore.preloader(false);
  };
}

export const publicationRegisterStore = new PublicationRegisterStore({
  api: cmApi,
});
